import { useQuery } from "@tanstack/react-query"
import { Pencil, Plus } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"

import { EditorEnte } from "./EditorEnte"
import { EditorObligacion } from "./EditorObligacion"
import { type Ente, etiquetaPeriodicidad, type Obligacion } from "./tipos"

/**
 * Los entes y sus obligaciones. Nace sembrado con los típicos de una agencia de aduanas
 * y es un PUNTO DE PARTIDA: las fechas cambian por providencia y por ordenanza, y se
 * corrigen aquí. La siembra no pisa lo corregido.
 */
export function CatalogoCumplimiento() {
  const [ente, setEnte] = useState<Ente | "nuevo" | null>(null)
  const [obligacion, setObligacion] = useState<{ fila: Obligacion | null; enteId?: number } | null>(
    null,
  )
  const entes = useQuery({
    queryKey: ["cumplimiento", "entes"],
    queryFn: () => api.get<Ente[]>("/api/v1/cumplimiento/entes"),
  })
  const obligaciones = useQuery({
    queryKey: ["cumplimiento", "obligaciones"],
    queryFn: () => api.get<Obligacion[]>("/api/v1/cumplimiento/obligaciones"),
  })

  if (entes.isPending || obligaciones.isPending) return <EstadoCarga />
  if (entes.isError || obligaciones.isError) {
    return (
      <EstadoError
        titulo="No se pudo leer el catálogo."
        error={entes.error ?? obligaciones.error}
        onReintentar={() => {
          entes.refetch()
          obligaciones.refetch()
        }}
      />
    )
  }

  return (
    <div>
      <p className="mb-3 text-sm" style={{ color: "var(--fg-muted)" }}>
        Es un punto de partida: confirma fechas y periodicidades con tu contador y con cada ente, y
        corrígelas aquí.
      </p>
      <div className="mb-3 flex justify-end">
        <Boton variante="primario" onClick={() => setEnte("nuevo")}>
          <Plus size={15} aria-hidden="true" />
          Nuevo ente
        </Boton>
      </div>
      <div className="grid gap-3">
        {entes.data.map((e) => (
          <section
            key={e.id}
            className="tarjeta p-4"
            style={{ opacity: e.activo ? undefined : 0.6 }}
          >
            <header className="mb-2 flex flex-wrap items-center gap-2">
              <h3 className="font-semibold">
                {e.codigo} · {e.nombre}
              </h3>
              <Insignia>{e.ambito.toLowerCase()}</Insignia>
              {!e.activo && <Insignia>Inactivo</Insignia>}
              <span className="ml-auto flex gap-1">
                <Boton
                  variante="fantasma"
                  tamano="sm"
                  aria-label={`Editar ${e.codigo}`}
                  onClick={() => setEnte(e)}
                >
                  <Pencil size={15} aria-hidden="true" />
                </Boton>
                <Boton
                  variante="fantasma"
                  tamano="sm"
                  onClick={() => setObligacion({ fila: null, enteId: e.id })}
                >
                  <Plus size={15} aria-hidden="true" />
                  Obligación
                </Boton>
              </span>
            </header>
            <ul className="grid gap-1 text-sm">
              {obligaciones.data
                .filter((o) => o.ente_id === e.id)
                .map((o) => (
                  <li
                    key={o.id}
                    className="flex flex-wrap items-center gap-x-3"
                    style={{ opacity: o.activa ? undefined : 0.6 }}
                  >
                    <button
                      type="button"
                      className="foco-cristal text-left underline-offset-2 hover:underline"
                      onClick={() => setObligacion({ fila: o })}
                    >
                      {o.nombre}
                    </button>
                    <span style={{ color: "var(--fg-muted)" }}>
                      {etiquetaPeriodicidad(o.periodicidad)}
                      {o.activa ? "" : " · inactiva"}
                    </span>
                  </li>
                ))}
            </ul>
          </section>
        ))}
      </div>

      {ente !== null && (
        <EditorEnte ente={ente === "nuevo" ? null : ente} onCerrar={() => setEnte(null)} />
      )}
      {obligacion !== null && (
        <EditorObligacion
          obligacion={obligacion.fila}
          enteInicial={obligacion.enteId}
          entes={entes.data}
          onCerrar={() => setObligacion(null)}
        />
      )}
    </div>
  )
}
